import dateFormat from 'dateformat';
import { startOfDay, endOfDay, subMonths, addMonths, differenceInHours, isToday, endOfMinute } from 'date-fns';
import { getEventDescription } from '#component/device/log/helper';
import { deviceSettingsEventType, shipmentEventType, warehouseEventType, projectEventType } from '#type';

const getMeasurementsPeriod = (
  dateFormatFn,
  { rangeStart, rangeEnd, rangeInterval }
) => ({
  start: dateFormatFn(rangeStart, 'isoUtcDateTime'),
  end: dateFormatFn(rangeEnd, 'isoUtcDateTime'),
  interval: rangeInterval
});

const createEventItem = item => {
  let eventItem;
  const { event: { id }, project, timestamp, shipment, appliance } = item;
  const settingsLogDescription = getEventDescription(item, '', true);

  try {
    switch (id) {
      case deviceSettingsEventType.EDIT_SETTINGS: {
        eventItem = {
          description: settingsLogDescription,
          identifier: `device_${deviceSettingsEventType.EDIT_SETTINGS}`,
          details: item,
          eventId: id
        };
        break;
      }
      case projectEventType.ADD_DEVICE: {
        eventItem = {
          description: `Added to the "${project.title.trim()}" project`,
          identifier: `project_${projectEventType.ADD_DEVICE}`,
          details: item,
          eventId: id
        };
        break;
      }
      case projectEventType.DELETE_DEVICE: {
        eventItem = {
          description: `Removed from the "${project.title.trim()}" project`,
          identifier: `project_${projectEventType.DELETE_DEVICE}`,
          details: item,
          eventId: id
        };
        break;
      }
      case shipmentEventType.ADD_DEVICE: {
        eventItem = {
          description: `Added to the "${shipment.label.trim()}" shipment`,
          identifier: 'shipment_40030',
          details: item,
          eventId: id
        };
        break;
      }
      case shipmentEventType.REMOVE_DEVICE: {
        eventItem = {
          description: `Removed from the "${shipment.label.trim()}" shipment`,
          identifier: 'shipment_40032',
          details: item,
          eventId: id
        };
        break;
      }
      case warehouseEventType.ADD_DEVICE: {
        eventItem = {
          description: `Added to the "${appliance.label.trim()}" application`,
          identifier: 'appliance_50040',
          details: item,
          eventId: id
        };
        break;
      }
      case warehouseEventType.REMOVE_DEVICE: {
        eventItem = {
          description: `Removed from the "${appliance.label.trim()}" application`,
          identifier: 'appliance_50042',
          details: item,
          eventId: id
        };
        break;
      }
      default: {
        eventItem = {
          description: 'Not recognized event',
          identifier: id,
          details: item,
          eventId: id
        };
        break;
      }
    }
  } catch (e) {
    eventItem = {
      description: 'Not recognized event',
      identifier: id,
      details: item,
      eventId: id
    };
  }

  return {
    details: {},
    timestamp: `${timestamp}`,
    ...eventItem
  };
};

export const formatProjectEvents = eventLogs => eventLogs.map(createEventItem);

export const reduceDuplica = events => {
  const reduced = [];
  const { length } = events;
  let cur = events[0];

  if (cur) {
    reduced.push(cur);
  }

  for (let i = 1; i < length; i += 1) {
    const next = events[i];
    if (
      // let's test removed duplicates awhile
      ['off', 'on'].indexOf(cur.identifier) !== -1 &&
      next.identifier === cur.identifier
    ) {
      if (!cur.duplica) {
        cur.duplica = 2;
      } else {
        cur.duplica += 1;
      }
    } else {
      cur = events[i];
      reduced.push(cur);
    }
  }
  return reduced;
};

export const getChartDateRange = (start, end) => {
  const minRangeDiffH = 2; // hours
  const minRangeDiff = minRangeDiffH * 1000 * 60 * 60; // milliseconds

  const rangeStart = start;
  let rangeEnd = end;

  if (+new Date(rangeEnd) - new Date(rangeStart) < minRangeDiff) {
    rangeEnd = new Date(rangeStart);
    rangeEnd.setHours(rangeEnd.getHours() + minRangeDiffH);
    rangeEnd = rangeEnd.toISOString();
  }

  const rangeInterval = Math.round((new Date(rangeEnd) - new Date(rangeStart)) / 8 / 1000);

  return { rangeStart, rangeEnd, rangeInterval };
};

export const getPeriod = ({
  lastMetric,
  lastEvent,
  lastDeviceLog = { timestamp: 0 },
  lastLocation = { timestamp: 0 },
  startDate,
  endDate,
  rangeInterval,
  beginDate,
  expirationDate,
  isDateSetFromUrl,
  dateRange = 0 * 24 * 60 * 60 * 1000,
  monthsDiff,
  withCurrentDay
}) => {
  let period = null;
  const lastMetricTimestamp = Object.values(lastMetric)
    .some(v => v?._) ? lastMetric.timestamp : 0;

  let timestamp = Math.max(
    +new Date(lastMetricTimestamp),
    +new Date(lastEvent.timestamp),
    +new Date(lastDeviceLog.timestamp),
    +new Date(lastLocation.timestamp)
  ) || +new Date();

  if (withCurrentDay) {
    const currentDate = new Date();
    if (differenceInHours(currentDate, new Date(timestamp)) < 24) {
      timestamp = currentDate;
    }
  }

  if (isDateSetFromUrl) {
    period = getMeasurementsPeriod(dateFormat, {
      rangeStart: startDate,
      rangeEnd: endDate,
      rangeInterval
    });
    return period;
  }

  if (timestamp && (
    !expirationDate ||
          (new Date(beginDate) <= new Date(timestamp) &&
            new Date(expirationDate) >= new Date(timestamp))
  )) {
    let date2 = new Date(timestamp);
    let date1 = monthsDiff ? subMonths(date2, monthsDiff) : new Date(+date2 - dateRange);
    const bDate = new Date(beginDate);
    // the `start date` should be before `creationDate` (project, appliance)
    if (bDate > date1) {
      date1 = bDate;
    }
    if (date1 > date2) {
      date2 = monthsDiff ? addMonths(date1, monthsDiff) : new Date(+date1 + dateRange);
    }
    const currentDate = new Date();
    if (date2 > new Date()) {
      date2 = currentDate;
    }

    period = getMeasurementsPeriod(dateFormat, {
      rangeStart: date1,
      rangeEnd: date2,
      rangeInterval: Math.round((date2 - date1) / 8 / 1000)
    });
  } else if (
    // if project date range was expired
    // set device current date range to the last day
    expirationDate &&
            new Date(expirationDate) < new Date()
  ) {
    const date1 = new Date(expirationDate);
    date1.setHours(0, 0, 1, 0);
    const date2 = new Date(expirationDate);

    period = getMeasurementsPeriod(dateFormat, {
      rangeStart: date1,
      rangeEnd: date2,
      rangeInterval: Math.round((date2 - date1) / 8 / 1000)
    });
  }

  const newStartDate = dateFormat(startOfDay(new Date(period.start)), 'isoUtcDateTime');
  const metricsEnd = new Date(period.end);
  const ofEnd = isToday(metricsEnd) ? endOfMinute : endOfDay;
  const newEndDate = dateFormat(ofEnd(metricsEnd), 'isoUtcDateTime');

  return {
    start: newStartDate,
    end: newEndDate
  };
};

export const getChartRange = item => {
  if (Array.isArray(item)) {
    return [
      startOfDay(new Date(item[0].timestamp)),
      endOfDay(new Date(item[0].timestamp))
    ];
  }

  return new Date(item.timestamp);
};
